import {Exclude, Expose, instanceToPlain, plainToInstance, Transform} from "class-transformer";
import * as t from "io-ts";
import {validateJSON, validateJSONNow} from "../web/validation";
import {Entity} from "./entity";
import {getDatabaseCodeTransformer} from "./transfomers/transformers";
import {TagDefinitionSourceKind, toTagDefinitionSourceKind} from "./tag-definition-source-kind";

export const TagDefinitionCodec = t.intersection([
  t.type({
    id: t.string,
    key: t.string,
    label: t.string,
    sourceKind: t.string,
    doNotUse: t.boolean,
  }),
  t.partial({
    description: t.string,
    code: t.string,
    colorHex: t.string,
    createdById: t.string,
    lastModifiedById: t.string,
    createDttm: t.number,
    lastModifiedDttm: t.number,
  }),
]);

export type TagDefinition = t.TypeOf<typeof TagDefinitionCodec>;

@Exclude()
export class TagDefinitionEntity implements Entity {
  @Expose()
  id!: string;

  @Expose()
  key!: string;

  @Expose()
  label!: string;

  @Expose()
  description?: string;

  @Expose()
  @Transform(getDatabaseCodeTransformer(toTagDefinitionSourceKind))
  sourceKind!: TagDefinitionSourceKind;

  @Expose()
  code?: string;

  @Expose()
  colorHex?: string;

  @Expose()
  doNotUse!: boolean;

  @Expose()
  createdById?: string;

  @Expose()
  lastModifiedById?: string;

  @Expose()
  createDttm?: number;

  @Expose()
  lastModifiedDttm?: number;

  readonly entityType = "TAGDEFINITION";

  get displayName(): Readonly<string> {
    return this.label ?? this.key ?? this.id;
  }

  constructor() {
  }

  static from(value: TagDefinition): TagDefinitionEntity {
    return plainToInstance(TagDefinitionEntity, value, {
      excludeExtraneousValues: true,
    });
  }

  loadTypeValue(): Promise<TagDefinition> {
    const object = instanceToPlain(this, {excludeExtraneousValues: true});
    return validateJSON(TagDefinitionCodec, object);
  }

  asTypeValue(): TagDefinition {
    const object = instanceToPlain(this, {excludeExtraneousValues: true});
    return validateJSONNow(TagDefinitionCodec, object);
  }
}

export const TagDefinitionUsageCodec = t.intersection([
  t.type({
    def: TagDefinitionCodec,
    usageCount: t.number,
  }),
  t.partial({

  }),
]);

export type TagDefinitionUsage = t.TypeOf<typeof TagDefinitionUsageCodec>;

export const TagDefinitionReplaceCategoriesRequestCodec = t.intersection([
  t.type({
    categoryIds: t.array(t.string),
  }),
  t.partial({}),
]);

export type TagDefinitionReplaceCategoriesRequest = t.TypeOf<typeof TagDefinitionReplaceCategoriesRequestCodec>;

export const normalizeTagInput = (value: string): string => {
  return value.trim().toLowerCase().replace(/\s+/g, " ");
}

export const normalizeTagDefinitionKey = (value: string): string => {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .replace(/_+/g, "_");
}

export const matchesTagDefinition = (tag: TagDefinitionEntity | TagDefinition, query: string): boolean => {
  if (!query.trim())
    return true;

  const q = normalizeTagInput(query);

  return (
    tag.label.toLowerCase().includes(q) ||
    tag.key.toLowerCase().includes(normalizeTagDefinitionKey(query))
  );
}

export interface TagDefinitionLike {
  id: string;
  key: string;
  label?: string;
}
